/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * DownloadMediaModal — EOS Download Media modal with folder/study tree selection.
 * Migrated from: lego/page/eos/download-media/DownloadMediaModal.jsx
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { FormLabel } from 'react-bootstrap'
import { useServiceContext } from '../../../root'
import Modal from '../../../components/Modal'
import FsiPicker from '../../../components/FsiPicker/FsiPicker'
import type { TreeNode } from '../../../components/FsiPickerNode/FsiPickerNode'
import aclService from '../../../services/acl.service'
import { FileAclLevel } from '../../../types/acl'

/**
 * Extended TreeNode to account for API-populated properties
 * that are not part of the base TreeNode interface but are returned
 * by the EOS folder tree endpoints.
 */
interface DownloadMediaTreeNode extends TreeNode {
  aclLevel?: string
  acl?: { level?: string }
}

const DownloadMediaModal: React.FC = () => {
  const context = useServiceContext()
  const selectedStdyRef = useRef<number>(0)
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(true)
  const [selection, setSelection] = useState<(number | string)[] | null>(null)
  const [rootFolderId, setRootFolderId] = useState<number | null>(null)
  const [baseNodeRef, setBaseNodeRef] = useState<TreeNode[] | null>(null)

  useEffect(() => {
    context.api.eos.getEosFolderId().then((data: unknown) => {
      setRootFolderId(data as number)
    })
  }, [])

  const isNodeSelectable = useCallback(
    (node: TreeNode): boolean => {
      const extNode = node as DownloadMediaTreeNode
      const aclLevel = extNode.aclLevel || extNode.acl?.level
      return (
        node.id !== rootFolderId &&
        aclService.isAbove(aclLevel as FileAclLevel, FileAclLevel.READ) &&
        (selectedStdyRef.current ? node.eosFilePicker?.studyId === selectedStdyRef.current : !selectedStdyRef.current)
      )
    },
    [rootFolderId]
  )

  const doClose = () => {
    setIsOpen(false)
  }

  const findNodeById = (node: TreeNode, id: number | string): TreeNode | null => {
    if (node.id === id) return node
    if (node.children) {
      for (const child of node.children) {
        const foundNode = findNodeById(child, id)
        if (foundNode) return foundNode
      }
    }
    return null
  }

  const findDescendantNodes = (node: TreeNode): Set<number | string> => {
    const descendantNodes = new Set<number | string>()

    const traverseNodes = (current: TreeNode) => {
      descendantNodes.add(current.id)
      if (current.children) {
        current.children.forEach((child) => traverseNodes(child))
      }
    }

    traverseNodes(node)
    return descendantNodes
  }

  const getFinalizedIds = (selectedIds: (number | string)[]): (number | string)[] => {
    const node = baseNodeRef![0]
    const descendantsToRemove = new Set<number | string>()

    selectedIds.forEach((id) => {
      const nodeToCheck = findNodeById(node, id)
      if (nodeToCheck) {
        const descendants = findDescendantNodes(nodeToCheck)
        descendants.forEach((descendantId) => {
          if (nodeToCheck.id !== descendantId) {
            descendantsToRemove.add(descendantId)
          }
        })
      }
    })

    return selectedIds.filter((id) => !descendantsToRemove.has(id))
  }

  const handleSubmit = () => {
    if (selection?.length && baseNodeRef) {
      let selectedIdsData = [...selection]
      selectedIdsData = selectedIdsData.filter((item) => item !== baseNodeRef[0]?.id)
      selectedIdsData = getFinalizedIds(selectedIdsData)

      context.api.eos
        .downloadMedia(selectedIdsData as number[], selectedStdyRef.current || null)
        .then(() => {
          context.service.toast.success('EOS_MEDIA_DOWNLOAD_MEDIA_SUCCESS_MSG')
          doClose()
        })
        .catch((error: { data?: { message?: string } }) => {
          context.service.toast.error(error?.data?.message ?? '')
          doClose()
        })
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('EOS_MEDIA_DOWNLOAD_MEDIA_TITLE')}
      className="theme-sandman"
    >
      <Modal.Body>
        <div className="fs12">
          <div className="download-media-picker">
            <FormLabel>{translate('EOS_SETUP_FOLDER')}</FormLabel>
            {rootFolderId && (
              <FsiPicker
                options={{
                  rootId: rootFolderId,
                  showRC: false,
                  showSystem: false,
                  eosMediaTransferMode: true,
                  showCheckBox: true,
                  onlyFoldersAndDocumentsWithWriteAccess: true,
                  selector: 'folder'
                }}
                onSelect={(fsi, node) => {
                  setSelection(fsi as (number | string)[])
                  setBaseNodeRef((node as TreeNode[] | undefined) ?? null)
                }}
                onSetStudyFolderId={(fsi) => {
                  selectedStdyRef.current = fsi
                }}
                isNodeSelectable={isNodeSelectable}
              />
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('CANCEL')}
        </Modal.Footer.Button>
        <Modal.Footer.Button variant="primary" onClick={handleSubmit} disabled={!selection}>
          {translate('EOS_MEDIA_DOWNLOAD_MEDIA_NOW_BTN')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DownloadMediaModal
